import { useState } from 'react'
import { DollarSign, FlaskConical, RefreshCw } from 'lucide-react'
import { useMe } from '../../api/auth'
import {
  type Period,
  useBillingByOrg,
  useBillingSummary,
  useRdSpend,
  useRunReconciliation,
} from '../../api/admin'

const PERIODS: { value: Period; label: string }[] = [
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'this_year', label: 'This year' },
  { value: 'all_time', label: 'All time' },
]

/**
 * Body of the original /admin page — cost / price / profit / margin
 * across orgs.
 *
 * Pricing knobs (per-minute rate, storage tiers) now live in the Stripe
 * tab's Pricing Config panel — single source of truth, no per-org markup
 * multiplier here anymore.
 */
export function AdminBillingOverviewTab() {
  const { data: me } = useMe()
  const [period, setPeriod] = useState<Period>('this_month')
  const [scope, setScope] = useState<'active' | 'all'>('active')
  const orgScope = scope === 'all' ? 'all' : undefined

  const activeOrgName =
    me?.memberships.find((m) => m.org_id === me.active_org_id)?.org?.name ??
    me?.active_org_id ??
    '—'

  const summary = useBillingSummary(period, orgScope)
  const orgs = useBillingByOrg(period)
  const rd = useRdSpend(period, orgScope)
  const reconcile = useRunReconciliation()

  const s = summary.data
  const fmtMoney = (n: number | null | undefined) =>
    n == null ? '—' : `$${n.toFixed(2)}`
  const fmtPct = (n: number | null | undefined) =>
    n == null ? '—' : `${n.toFixed(1)}%`

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-end gap-2">
        <div className="inline-flex overflow-hidden rounded-lg border border-border bg-surface">
          <button
            onClick={() => setScope('active')}
            className={`px-3 py-2 text-sm transition-colors ${
              scope === 'active'
                ? 'bg-primary/15 text-primary'
                : 'text-text-secondary hover:bg-surface-elevated'
            }`}
            title="Show only the org currently selected in the header dropdown"
          >
            {activeOrgName}
          </button>
          <button
            onClick={() => setScope('all')}
            className={`border-l border-border px-3 py-2 text-sm transition-colors ${
              scope === 'all'
                ? 'bg-primary/15 text-primary'
                : 'text-text-secondary hover:bg-surface-elevated'
            }`}
            title="Aggregate across every organization (superadmin only)"
          >
            All orgs
          </button>
        </div>
        <select
          value={period}
          onChange={(e) => setPeriod(e.target.value as Period)}
          className="rounded-lg border border-border bg-surface px-3 py-2 text-sm"
        >
          {PERIODS.map((p) => (
            <option key={p.value} value={p.value}>{p.label}</option>
          ))}
        </select>
        <button
          onClick={() => reconcile.mutate()}
          disabled={reconcile.isPending}
          className="inline-flex items-center gap-1 rounded-lg border border-border bg-surface px-3 py-2 text-sm hover:bg-surface-elevated disabled:opacity-50"
          title="Pull the last 7 days of Twilio usage records"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${reconcile.isPending ? 'animate-spin' : ''}`} />
          Reconcile Twilio
        </button>
      </div>

      <section className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <StatCard
          label="Cost (you pay)"
          value={fmtMoney(s?.cost_usd)}
          hint="Minutes + storage"
          subLines={s ? [
            { label: 'Minutes', value: fmtMoney(s.minutes_cost_usd) },
            { label: 'Storage', value: fmtMoney(s.storage_cost_usd) },
          ] : undefined}
        />
        <StatCard
          label="Price (you charge)"
          value={fmtMoney(s?.price_usd)}
          hint="Minutes + storage"
          subLines={s ? [
            { label: 'Minutes', value: fmtMoney(s.minutes_price_usd) },
            { label: 'Storage', value: fmtMoney(s.storage_price_usd) },
          ] : undefined}
        />
        <StatCard
          label="Profit"
          value={fmtMoney(s?.profit_usd)}
          hint={`Margin ${fmtPct(s?.margin_pct)}`}
          tone={s && s.profit_usd >= 0 ? 'success' : 'warning'}
        />
        <StatCard
          label="Volume"
          value={s ? `${s.call_count} calls` : '—'}
          hint={s ? `${s.minutes.toFixed(1)} min` : ''}
          subLines={s ? [
            { label: 'Storage', value: `${s.storage_gb.toFixed(2)} GB` },
          ] : undefined}
        />
      </section>

      {s && Object.keys(s.breakdown).length > 0 && (
        <section className="rounded-lg border border-border bg-surface p-4">
          <header className="mb-3 flex items-center gap-2 font-medium">
            <DollarSign className="h-4 w-4 text-text-secondary" /> Cost by provider
          </header>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            {Object.entries(s.breakdown).map(([provider, row]) => (
              <div key={provider} className="rounded border border-border bg-background p-3">
                <div className="text-xs uppercase tracking-wide text-text-secondary">{provider}</div>
                <div className="mt-1 font-mono text-sm">${row.cost_usd.toFixed(4)}</div>
                <div className="mt-0.5 text-xs text-text-secondary">
                  {provider === 'storage'
                    ? `${s.storage_gb.toFixed(2)} GB stored`
                    : `${row.call_count} rows · ${row.minutes.toFixed(1)} min`}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="rounded-lg border border-border bg-surface p-4">
        <header className="mb-3 flex items-center gap-2 font-medium">
          <FlaskConical className="h-4 w-4 text-text-secondary" /> Internal / R&amp;D spend
        </header>
        <div className="flex flex-wrap items-baseline gap-6">
          <div>
            <div className="text-xs text-text-secondary">Cost</div>
            <div className="font-mono text-lg">{fmtMoney(rd.data?.cost_usd)}</div>
          </div>
          <div>
            <div className="text-xs text-text-secondary">Minutes</div>
            <div className="font-mono text-lg">{rd.data ? rd.data.minutes.toFixed(1) : '—'}</div>
          </div>
          <div>
            <div className="text-xs text-text-secondary">Calls</div>
            <div className="font-mono text-lg">{rd.data?.call_count ?? '—'}</div>
          </div>
          <p className="text-xs text-text-secondary">
            Simulation / AI-to-AI calls. Real money, but excluded from margin math since you&rsquo;re not billing a customer for them.
          </p>
        </div>
      </section>

      {scope === 'all' && (
        <section className="overflow-hidden rounded-lg border border-border bg-surface">
          <header className="border-b border-border px-4 py-3 font-medium">By organization</header>
          {!orgs.data || orgs.data.orgs.length === 0 ? (
            <div className="p-6 text-center text-sm text-text-secondary">
              No billable activity in this period.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-surface-elevated text-xs uppercase text-text-secondary">
                  <tr>
                    <th className="px-4 py-2 text-left">Org</th>
                    <th className="px-4 py-2 text-right">Calls</th>
                    <th className="px-4 py-2 text-right">Minutes</th>
                    <th className="px-4 py-2 text-right">Cost</th>
                    <th className="px-4 py-2 text-right">Price</th>
                    <th className="px-4 py-2 text-right">Profit</th>
                    <th className="px-4 py-2 text-right">Margin</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {orgs.data.orgs.map((o) => (
                    <tr key={o.org_id}>
                      <td className="px-4 py-2">
                        <div className="font-medium">{o.org_name ?? o.org_id}</div>
                        <div className="text-xs text-text-secondary">{o.org_slug ?? ''}</div>
                      </td>
                      <td className="px-4 py-2 text-right font-mono">{o.call_count}</td>
                      <td className="px-4 py-2 text-right font-mono">{o.minutes.toFixed(1)}</td>
                      <td className="px-4 py-2 text-right font-mono">${o.cost_usd.toFixed(4)}</td>
                      <td className="px-4 py-2 text-right font-mono">${o.price_usd.toFixed(4)}</td>
                      <td className={`px-4 py-2 text-right font-mono ${o.profit_usd >= 0 ? 'text-success' : 'text-error'}`}>
                        ${o.profit_usd.toFixed(4)}
                      </td>
                      <td className="px-4 py-2 text-right font-mono">
                        {o.margin_pct == null ? '—' : `${o.margin_pct.toFixed(1)}%`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

    </div>
  )
}

function StatCard({
  label, value, hint, tone, subLines,
}: {
  label: string
  value: string
  hint?: string
  tone?: 'success' | 'warning'
  // Optional sub-lines under the headline value for a "X (─ A $.. ─ B $..)"
  // breakdown view. Used to surface minutes-vs-storage on the COST/PRICE
  // cards without bolting on a fourth card.
  subLines?: Array<{ label: string; value: string }>
}) {
  const toneClass =
    tone === 'success' ? 'text-success' : tone === 'warning' ? 'text-warning' : ''
  return (
    <div className="rounded-lg border border-border bg-surface p-4">
      <div className="text-xs uppercase tracking-wide text-text-secondary">{label}</div>
      <div className={`mt-1 font-mono text-2xl font-semibold ${toneClass}`}>{value}</div>
      {hint && <div className="mt-1 text-xs text-text-secondary">{hint}</div>}
      {subLines && subLines.length > 0 && (
        <div className="mt-2 space-y-0.5">
          {subLines.map((line) => (
            <div key={line.label} className="flex items-center justify-between text-xs">
              <span className="text-text-secondary">─ {line.label}</span>
              <span className="font-mono">{line.value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
